import { useState, useEffect, useCallback, useRef } from 'react';

interface UseDragAndDropProps {
  onDrop: (files: File[]) => void;
  isVisible?: boolean;
}

export const useDragAndDrop = ({ onDrop, isVisible = true }: UseDragAndDropProps) => {
  const [isDragOver, setIsDragOver] = useState(false);

  // Use a ref to track drag counter to avoid stale closures
  const dragCounterRef = useRef(0);

  const handleDragEnter = useCallback((e: DragEvent) => {
    e.preventDefault();
    if (!isVisible) {
      setIsDragOver(false);
      return;
    }
    dragCounterRef.current += 1;
    setIsDragOver(true);
  }, [isVisible]);

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    if (!isVisible) {
      setIsDragOver(false);
      return;
    }
    setIsDragOver(true);
  }, [isVisible]);

  const handleDragLeave = useCallback((e: DragEvent) => {
    if (!isVisible) {
      setIsDragOver(false);
      dragCounterRef.current = 0;
      return;
    }
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      setIsDragOver(false);
      dragCounterRef.current = 0;
    }
  }, [isVisible]);

  const handleDrop = useCallback((e: DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    dragCounterRef.current = 0;
    
    if (!isVisible) {
      return;
    }

    if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      onDrop(Array.from(e.dataTransfer.files));
    }
  }, [onDrop, isVisible]);

  useEffect(() => {
    window.addEventListener('dragenter', handleDragEnter);
    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('drop', handleDrop);

    return () => {
      window.removeEventListener('dragenter', handleDragEnter);
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('dragleave', handleDragLeave);
      window.removeEventListener('drop', handleDrop);
    };
  }, [handleDragEnter, handleDragOver, handleDragLeave, handleDrop]);

  return { isDragOver };
}; 